import {Bracket} from "../../../../data/models/bracket_api/Bracket";
import {Result} from "../../../../data/models/bracket_api/Result";
import {RaceClass} from "../../../../data/models/bracket_api/RaceClass";
import {Driver} from "../../../../data/models/bracket_api/Driver";
import {Event} from "../../../../data/models/bracket_api/Event";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import {useTheme} from "@mui/material";
import ConfigBlockTopMoments from "./ConfigBlockTopMoments";
import BracketConnector from "../../BracketConnector";
import Typography from "@mui/material/Typography";
import BracketConnectorReverse from "../../BracketConnectorReverse";
import ConfigBlockReverseTopMoments from "./ConfigBlockReverseTopMoments";

export type ConfigBracketTopMomentsProps = {
    quadrant?: number, // 0=TopLeft, 1=TopRight, 2=BottomLeft, 3=BottomRight, undefined=center
    bracket?: Bracket,
    results?: Result[],
    updateResult: (results: Result) => void,
    event?: Event,
}

export default function ConfigBracketTopMoments(props: Readonly<ConfigBracketTopMomentsProps>) {
    let winner: Driver | undefined;
    const theme = useTheme();
    const connectorColor = theme.palette.mode === 'dark' ? '#ffffff' : '#000000';
    
    // If quadrant is undefined, this is the center section (rounds 5-6)
    if (props.quadrant === undefined) {
        winner = props.results?.find(result => {
            return result.round === 6 && result.winner
        })?.winner;
        
        return (
            <Grid container sx={{
                display: 'flex',
                flexDirection: "row",
                position: "relative",
                minWidth: 'fit-content',
                flexWrap: "nowrap",
            }}>
                {/* Round 5 - Final Four Game 1 (left side) */}
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    alignContent: "center"
                }}>
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 5 && result.game === 1
                        })}
                        updateResult={props.updateResult}
                    />
                </Grid>
                <Grid container sx={{
                    flex: .1,
                    display: 'flex',
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center"
                }}>
                    {/* Straight horizontal line connector */}
                    <hr style={{
                        width: '100%',
                        border: 'none',
                        borderTop: `2px solid ${connectorColor}`,
                        margin: 0
                    }} />
                </Grid>
                {/* Round 6 - Championship (center) */}
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                }}>
                    {props.event?.left_sponsor_image_link && (
                        <Box sx={{
                            position: "absolute",
                            top: "-300px",
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}>
                            <img 
                                src={props.event.left_sponsor_image_link} 
                                alt={"Sponsor Logo"} 
                                style={{maxHeight: '250px', maxWidth: '250px'}}
                            />
                        </Box>
                    )}
                    {winner && (
                        <Typography variant="h6" component="div" textAlign={"center"} position={"absolute"} top={"-80px"} width={"100%"}>
                            Winner: {winner.first_name + " " + winner.last_name}
                        </Typography>
                    )}
                    <ConfigBlockTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 6 && result.game === 1
                        })}
                        updateResult={props.updateResult}
                    />
                    {props.event?.right_sponsor_image_link && (
                        <Box sx={{
                            position: "absolute",
                            bottom: "-300px",
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}>
                            <img 
                                src={props.event.right_sponsor_image_link} 
                                alt={"Sponsor Logo"} 
                                style={{maxHeight: '250px', maxWidth: '250px'}}
                            />
                        </Box>
                    )}
                </Grid>
                <Grid container sx={{
                    flex: .1,
                    display: 'flex',
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center"
                }}>
                    {/* Straight horizontal line connector */}
                    <hr style={{
                        width: '100%',
                        border: 'none',
                        borderTop: `2px solid ${connectorColor}`,
                        margin: 0
                    }} />
                </Grid>
                {/* Round 5 - Final Four Game 2 (right side) */}
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    alignContent: "center"
                }}>
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 5 && result.game === 2
                        })}
                        updateResult={props.updateResult}
                    />
                </Grid>
            </Grid>
        );
    }
    
    // Render a quadrant (16-entry bracket, rounds 1-4)
    // Calculate game numbers for this quadrant
    // Quadrant 0: Round 1 games 1-8, Round 2 games 1-4, Round 3 games 1-2, Round 4 game 1
    // Quadrant 1: Round 1 games 9-16, Round 2 games 5-8, Round 3 games 3-4, Round 4 game 2
    // Quadrant 2: Round 1 games 17-24, Round 2 games 9-12, Round 3 games 5-6, Round 4 game 3
    // Quadrant 3: Round 1 games 25-32, Round 2 games 13-16, Round 3 games 7-8, Round 4 game 4
    
    const round1StartGame = props.quadrant * 8 + 1;
    const round2StartGame = props.quadrant * 4 + 1;
    const round3StartGame = props.quadrant * 2 + 1;
    const round4Game = props.quadrant + 1;
    
    // Use reverse layout for quadrants 1 and 3 (right side)
    const useReverse = props.quadrant === 1 || props.quadrant === 3;
    
    if (useReverse) {
        return (
            <Grid container sx={{
                display: 'flex',
                flexDirection: "row",
                position: "relative",
                minWidth: 'fit-content',
                flexWrap: "nowrap",
            }}>
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                }}>
                    {/* Round 4 */}
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 4 && result.game === round4Game
                        })}
                        updateResult={props.updateResult}
                    />
                </Grid>
                <Grid container sx={{
                    flex: .1,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-evenly",
                    alignItems: "center"
                }}>
                    <BracketConnectorReverse height={"51%"}/>
                </Grid>
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    alignContent: "center"
                }}>
                    {/* Round 3 */}
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 3 && result.game === round3StartGame
                        })}
                        updateResult={props.updateResult}
                    />
                    {/* Spacer to replace emblem for proper spacing */}
                    <Box sx={{width: '270px', height: '270px'}} />
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 3 && result.game === round3StartGame + 1
                        })}
                        updateResult={props.updateResult}
                    />
                </Grid>
                <Grid container sx={{
                    flex: .1,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-around",
                    alignItems: "center"
                }}>
                    <BracketConnectorReverse height={"25.5%"}/>
                    <BracketConnectorReverse height={"25.5%"}/>
                </Grid>
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-around",
                    alignItems: "center",
                    height: '100%',
                }}>
                    {/* Round 2 */}
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 2 && result.game === round2StartGame
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 2 && result.game === round2StartGame + 1
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 2 && result.game === round2StartGame + 2
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 2 && result.game === round2StartGame + 3
                        })}
                        updateResult={props.updateResult}
                    />
                </Grid>
                <Grid container sx={{
                    flex: .1,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-around",
                    alignItems: "center",
                    height: '100%',
                }}>
                    <BracketConnectorReverse height={"12.75%"}/>
                    <BracketConnectorReverse height={"12.75%"}/>
                    <BracketConnectorReverse height={"12.75%"}/>
                    <BracketConnectorReverse height={"12.75%"}/>
                </Grid>
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-evenly",
                    alignItems: "center",
                    height: '100%',
                }}>
                    {/* Round 1 */}
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 1
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 2
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 3
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 4
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 5
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 6
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockReverseTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 7
                        })}
                        updateResult={props.updateResult}
                    />
                </Grid>
            </Grid>
        );
    } else {
        // Forward layout for quadrants 0 and 2 (left side)
        return (
            <Grid container sx={{
                display: 'flex',
                flexDirection: "row",
                position: "relative",
                minWidth: 'fit-content',
                flexWrap: "nowrap",
            }}>
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-evenly",
                    alignItems: "center",
                    height: '100%',
                }}>
                    {/* Round 1 */}
                    <ConfigBlockTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 1
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 2
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 3
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 4
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 5
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 6
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 1 && result.game === round1StartGame + 7
                        })}
                        updateResult={props.updateResult}
                    />
                </Grid>
                <Grid container sx={{
                    flex: .1,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-around",
                    alignItems: "center",
                    height: '100%',
                }}>
                    <BracketConnector height={"12.75%"}/>
                    <BracketConnector height={"12.75%"}/>
                    <BracketConnector height={"12.75%"}/>
                    <BracketConnector height={"12.75%"}/>
                </Grid>
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-around",
                    alignItems: "center",
                    height: '100%',
                }}>
                    {/* Round 2 */}
                    <ConfigBlockTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 2 && result.game === round2StartGame
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 2 && result.game === round2StartGame + 1
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 2 && result.game === round2StartGame + 2
                        })}
                        updateResult={props.updateResult}
                    />
                    <ConfigBlockTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 2 && result.game === round2StartGame + 3
                        })}
                        updateResult={props.updateResult}
                    />
                </Grid>
                <Grid container sx={{
                    flex: .1,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-around",
                    alignItems: "center"
                }}>
                    <BracketConnector height={"25.5%"}/>
                    <BracketConnector height={"25.5%"}/>
                </Grid>
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    alignContent: "center"
                }}>
                    {/* Round 3 */}
                    <ConfigBlockTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 3 && result.game === round3StartGame
                        })}
                        updateResult={props.updateResult}
                    />
                    {/* Spacer to replace emblem for proper spacing */}
                    <Box sx={{width: '270px', height: '270px'}} />
                    <ConfigBlockTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 3 && result.game === round3StartGame + 1
                        })}
                        updateResult={props.updateResult}
                    />
                </Grid>
                <Grid container sx={{
                    flex: .1,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "space-evenly",
                    alignItems: "center"
                }}>
                    <BracketConnector height={"51%"}/>
                </Grid>
                <Grid container sx={{
                    flex: .2,
                    display: 'flex',
                    flexDirection: "column",
                    justifyContent: "center",
                    position: "relative",
                }}>
                    {/* Round 4 */}
                    <ConfigBlockTopMoments
                        result={props.results?.find((result) => {
                            return result.round === 4 && result.game === round4Game
                        })}
                        updateResult={props.updateResult}
                    />
                </Grid>
            </Grid>
        );
    }
}
